<template>
  <base-review-member-popup
    class="invite-user-popup"
    title="User Management"
    @close="$emit('close')"
  >
    <search-bar
      v-model="searchInput"
      :label="searchInputLabel"
      :debounce="200"
    />
    <invite-user-list
      class="mt-2"
      :search-input="searchInput"
      :lead-reviewer="leadReviewer"
      :reviewers="reviewers"
      :invites="invites"
      :researcher-search-results="researcherSearchResults"
      :editable="editable"
      @invite="$emit('invite', $event)"
      @delete="$emit('delete', $event)"
      @revoke-invite="$emit('revoke-invite', $event)"
    />
    <div
      v-if="validEmailInInput && editable"
      class="send-invite-link d-flex align-center primary--text pt-3"
      @click="$emit('invite-by-email', researcherSearchInput)"
    >
      <v-icon class="material-icons-outlined mr-1" color="primary">
        mail
      </v-icon>
      Send invite to
      {{ researcherSearchInput }}
    </div>
  </base-review-member-popup>
</template>

<script>
import BaseReviewMemberPopup from './BaseReviewMemberPopup';
import InviteUserList from './InviteUserList';
import SearchBar from '@/components/Common/SearchBar';
import {
  ReviewMember,
  ReviewMemberInvite,
  Researcher,
} from '@/models';
import { typeHelpers, EMAIL_REGEX } from '@/modules';

export default {
  name: 'InviteUserPopup',
  components: { BaseReviewMemberPopup, InviteUserList, SearchBar },
  props: {
    leadReviewer: {
      type: ReviewMember,
      required: true,
    },
    reviewers: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, ReviewMember),
    },
    invites: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, ReviewMemberInvite),
    },
    researcherSearchInput: {
      type: String,
      default: '',
    },
    researcherSearchResults: {
      type: Array,
      default: () => [],
      validator: (val) => typeHelpers.isArrayOfType(val, Researcher),
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'invite',
    'invite-by-email',
    'delete',
    'revoke-invite',
    'update:researcherSearchInput',
    'close',
  ],
  computed: {
    searchInput: {
      get: function () {
        return this.researcherSearchInput;
      },
      set: function (value) {
        this.$emit('update:researcherSearchInput', value);
      },
    },
    searchInputLabel() {
      return this.editable
        ? 'Invite users by name or email...'
        : 'Search users by name or email...';
    },
    validEmailInInput() {
      return (
        EMAIL_REGEX.test(this.searchInput) &&
        !this.emailInInputAlreadyInvited &&
        !this.emailInInputAlreadyReviewer
      );
    },
    emailInInputAlreadyInvited() {
      return this.invites.some((invite) =>
        this.researcherSearchInput
          ?.toLowerCase()
          .includes(invite.emailAddress.toLowerCase())
      );
    },
    emailInInputAlreadyReviewer() {
      const allReviewers = [this.leadReviewer, ...this.reviewers];
      return allReviewers.some((reviewer) =>
        this.researcherSearchInput
          ?.toLowerCase()
          .includes(reviewer.researcher.email.toLowerCase())
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.send-invite-link {
  cursor: pointer;
}
</style>
